import React from 'react';
import {
  getOtherOptionValue,
  isOtherOptionValue,
} from '../../shared/forms/otherFieldHelpers';
import {
  RegistryFieldBoundary,
  useRegistryFieldProps,
  useRegistryOptions,
  useRegistryPlaceholder,
  useRegistrySectionTitle,
} from '../../Admin/FoundationalTables/FormRegistry/registryFieldOverrides';

// Shared visual primitives for the reusable step-wizard family (same flat/gray
// language as LivestockAddRecord.jsx / MarketAddRecord.jsx). Kept as a
// standalone file so new wizards (e.g. Form Registry's Create Form flow) can
// reuse it without touching those two already-working, live-backend forms.

export const INP =
  'w-full border-0 rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] ' +
  'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white appearance-none';

export const INP_LOCKED =
  'w-full border-0 rounded-lg px-4 py-3 text-sm text-gray-400 bg-gray-50 cursor-not-allowed appearance-none';

export const LBL = 'block text-[13px] font-bold text-gray-900 mb-1.5';

// Label / required / hint follow any edit made to this field in Form
// Registry (see registryFieldOverrides.jsx); a Select inside picks up the
// field's edited options through RegistryFieldBoundary.
export function Field({ label: pageLabel, required: pageRequired, hint: pageHint, children }) {
  const { label: labelText, required, hint, match } = useRegistryFieldProps(pageLabel, { required: pageRequired, hint: pageHint });
  return (
    <div>
      <label className={LBL}>
        {labelText}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <RegistryFieldBoundary match={match}>{children}</RegistryFieldBoundary>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function normalizeOption(option) {
  if (option === null || option === undefined) return null;
  if (typeof option === 'string' || typeof option === 'number') {
    const text = String(option).trim();
    return text ? { value: text, label: text } : null;
  }
  const value = String(option.value ?? option.id ?? option.code ?? option.label ?? option.name ?? '').trim();
  if (!value) return null;
  const label = String(option.label ?? option.name ?? option.title ?? value).trim() || value;
  return { value, label };
}

export function Select({ value, onChange, disabled, options: pageOptions = [], placeholder: pagePlaceholder = 'Select...', className = INP }) {
  const options = useRegistryOptions(pageOptions);
  const placeholder = useRegistryPlaceholder(pagePlaceholder);
  const normalizedOptions = options.map(normalizeOption).filter(Boolean);
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`${className} pr-9`}
        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
      >
        <option value="">{placeholder}</option>
        {normalizedOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

export function SelectWithOther({
  label: labelText,
  required,
  hint,
  value,
  otherValue = '',
  onChange,
  onOtherChange,
  disabled,
  options: pageOptions = [],
  placeholder = 'Select...',
  otherPlaceholder = 'Please specify',
  className = INP,
  otherClassName = INP,
}) {
  const { options } = useRegistryFieldProps(labelText, { options: pageOptions });
  const normalizedOptions = options.map(normalizeOption).filter(Boolean);
  const showOther = isOtherOptionValue(value, normalizedOptions);
  const otherOptionValue = getOtherOptionValue(normalizedOptions);

  return (
    <Field label={labelText} required={required} hint={hint}>
      <div className="space-y-2">
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          options={normalizedOptions}
          placeholder={placeholder}
          className={className}
        />
        {showOther && (
          <input
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            disabled={disabled}
            placeholder={otherPlaceholder || `Specify ${otherOptionValue}`}
            className={otherClassName}
          />
        )}
      </div>
    </Field>
  );
}

export function SectionHead({ title: pageTitle }) {
  const title = useRegistrySectionTitle(pageTitle);
  return <span className="block text-xs font-bold uppercase tracking-wider text-gray-500">{title}</span>;
}

export function SectionDivider() {
  return <div className="h-px w-full bg-green-200" />;
}

// Money input from the Add New Record design: a solid green "₦" block joined
// to the left of the field. Used for every NGN amount on the module forms.
export function CurrencyInput({ value, onChange, placeholder = '0', disabled = false, readOnly = false, min = '0', className = INP }) {
  return (
    <div className="flex">
      <span className="flex w-11 shrink-0 items-center justify-center rounded-l-lg bg-green-600 text-sm font-bold text-white">₦</span>
      <input
        type="number"
        min={min}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={`${disabled || readOnly ? INP_LOCKED : className} rounded-l-none`}
      />
    </div>
  );
}

export function isCurrencyLabel(label) {
  return /\(NGN\)|\(₦\)|\bNGN\b/.test(String(label ?? ''));
}

export function ReadonlyBox({ children }) {
  return (
    <div className="flex items-center min-h-[2.75rem] px-4 py-3 rounded-lg bg-gray-50 text-xs text-gray-400 italic">
      {children}
    </div>
  );
}

